// engine-progress.ts — the customer's "what's happening" words for an engine run (pure; tests without the API client).
// The stages a customer would recognise, in order, and which engine steps belong to each. The run's REAL progress
// (done / total step rows, the step it is on) comes from the workers' filmstrip; this only names it.
export interface RunProgress { done: number; total: number; currentStepId: string | null; failed: number }
import { w, type PageWordKey } from "@/lib/words.ts";

/** (B107) a stage's words are read through w() when they're shown (Admin → Words edits them): page.stage.<id>, <id>_now */
const stage = (id: string, match: RegExp, you?: boolean) => ({
  id, match, ...(you ? { you } : {}),
  get label() { return w(`page.stage.${id}` as PageWordKey); },
  get now() { return w(`page.stage.${id}_now` as PageWordKey); },
});

export const STAGES: { id: string; readonly label: string; readonly now: string; you?: boolean; match: RegExp }[] = [
  stage("plan", /^(analyze|research|strategy|brief|audience|casting|format|viral_type|hook_select)/),
  stage("script", /^(script|hook|beats|cta|narrative|story)/),
  stage("you_script", /^gate_script$/, true),
  stage("frames", /^(image|generate_image|generate_end_image|cover|render_cards|caption|character|continuity|still|motion_direct|sound_direct|presenter_direct|preview_frames|animatic|choreograph|voice_direct)/),
  // (owner's run 0b011d6f, 2026-10-03: motion_direct / sound_direct are pre-production DIRECTION — they run before the
  //  storyboard gate — but matched "clips", so the page ticked "Your review: the storyboard" before it had even opened.
  //  Directing a beat's motion, sound or presenter is part of making the storyboard; generating them comes after.
  //  The ANIMATIC too — the storyboard preview the gate waits for: while it renders, the page said "waiting for you to
  //  approve the storyboard" with nothing to approve yet. Only the open gate is the customer's turn.)
  // (creative-1, review J4) the choreographer and the voice director are direction before the storyboard, like
  //  motion_direct; the final screening is part of putting the video together.
  stage("you_storyboard", /^gate_storyboard$/, true),
  stage("clips", /^(motion|plan_takes|generate_clip|presenter|sound|generate_voice|conform_voice|music|compile_motion|measure_anchors|preview$|supply_broll|performance_sync)/),
  // (owner's run 0b011d6f, after approving the storyboard: the step on hand was compile_motion / supply_broll / the compare
  //  steps, which matched no stage, so the page jumped back to "Plan the video from your strategy" at 71 of 126.)
  stage("render", /^(render|publish_direct|publish_verify|viral_dna|pp_|post|compare_executions|gate_compare|apply_compare_winner|distribute|final_screening)/),
  stage("you_final", /^gate_final$/, true),
];

/** Which stage the run is in, from the step it is on. Unknown steps keep the last known stage. Pure. */
export function stageOf(stepId: string | null, fallback = 0): number {
  if (!stepId) return fallback;
  const i = STAGES.findIndex((s) => s.match.test(stepId));
  return i >= 0 ? i : fallback;
}

/** The percentage the bar shows: done / total step rows — or null before the run has rows (the bar then shows activity). */
export function progressPct(p: RunProgress | null | undefined): number | null {
  if (!p || !p.total) return null;
  return Math.max(0, Math.min(100, Math.round((p.done / p.total) * 100)));
}
